import { useContext, useEffect, useRef } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import {
	OnNotificationReceivedDocument,
	type OnNotificationReceivedSubscription
} from "~/gql/subscriptions/notification.generated";
import describeSubscriptionClose from "~/helpers/describeSubscriptionClose";
import logger from "~/helpers/logger";
import { useAppDispatch } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useSubscriptionClient from "~/hooks/useSubscriptionClient";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { recreateSubscriptionClientOnUnauthorized } from "~/services/subscriptionClient";

const useNotificationSubscription = (): void => {
	const subscriptionClient = useSubscriptionClient();
	const dispatch = useAppDispatch();
	const { isCustomer, loggedIn } = useContext(AuthContext);
	const { isEnabled } = useFeatureFlag(FeatureFlagName.Notifications);
	const sawDisconnect = useRef(false);

	useEffect(() => {
		if (!loggedIn || isCustomer || !isEnabled) return;

		const refetchNotifications = () => {
			dispatch(apiEndpoints.util.invalidateTags(["Notifications"]));
		};

		const disposeClosed = subscriptionClient.on("closed", () => {
			sawDisconnect.current = true;
		});
		const disposeConnected = subscriptionClient.on("connected", () => {
			if (!sawDisconnect.current) return;
			sawDisconnect.current = false;
			refetchNotifications();
		});

		const cleanup = subscriptionClient.subscribe<OnNotificationReceivedSubscription>(
			{ query: OnNotificationReceivedDocument.toString() },
			{
				complete: () => {},
				error: (err) => {
					const closed = describeSubscriptionClose(err);
					recreateSubscriptionClientOnUnauthorized(closed.code);
					logger.error(
						`[Notification subscription] close code=${closed.code ?? "none"} clean=${closed.wasClean ?? "n/a"} reason=${closed.reason}`,
						err,
						{ ...closed }
					);
					refetchNotifications();
				},
				// The event names the row and nothing else, so every signal is a refetch.
				next: () => refetchNotifications()
			}
		);

		return () => {
			disposeClosed();
			disposeConnected();
			cleanup();
		};
	}, [isCustomer, isEnabled, loggedIn, subscriptionClient]);
};

export default useNotificationSubscription;
